import { useState } from "react";
import { formatWeight } from "../utils/format.js";
import { leadAmong } from "../utils/analytics.js";
import { accentVars } from "../utils/palette.js";
import styles from "./ComparisonExercises.module.css";

// The handle whatever the column count: nothing on the screen labels a column
// A or B, so the two-profile shorthand named something the reader could not
// find. The accent tint is what says "leads".
function LeadBadge({ profiles, lead }) {
  if (!lead) return <span className={`${styles.lead} ${styles.flat}`}>equal</span>;
  const profile = profiles[lead.index];
  return (
    <span className={`${styles.lead} ${styles.leadOn}`} style={accentVars(profile.accent)}>
      @{profile.username}
    </span>
  );
}

function avgVolume(ex, weightUnit) {
  return formatWeight(ex.volumeKg / Math.max(ex.workoutCount, 1), weightUnit);
}

function workoutsLabel(n) {
  return `${n} workout${n !== 1 ? "s" : ""}`;
}

// Past two profiles the columns stop being self-explanatory, so each block
// carries its own handle instead of leaning on a header row.
function ExStats({ ex, profile, weightUnit, align, showName }) {
  return (
    <div className={align === "left" ? styles.bStats : styles.aStats} style={accentVars(profile.accent)}>
      {showName && <span className={styles.statName}>@{profile.username}</span>}
      <span className={styles.statVal}>{formatWeight(ex.volumeKg, weightUnit)}</span>
      <span className={styles.statSub}>{avgVolume(ex, weightUnit)} avg</span>
      <span className={styles.statSub}>{workoutsLabel(ex.workoutCount)}</span>
    </div>
  );
}

export default function ComparisonExercises({ profiles, weightUnit, periodDays }) {
  const [sortMode, setSortMode] = useState("volume");

  if (!profiles?.length) return null;

  const pair = profiles.length === 2;
  const frequencies = profiles.map((profile) => profile.analytics.exerciseFrequency || []);
  const maps = frequencies.map((freq) => Object.fromEntries(freq.map((e) => [e.name, e])));

  const avg = (ex) => ex.volumeKg / Math.max(ex.workoutCount, 1);
  // Only exercises every athlete in the set logged: with four profiles a row
  // missing one of them would have nothing to compare.
  const commonNames = frequencies[0].filter((e) => maps.every((m) => m[e.name])).map((e) => e.name);
  // Winner is decided by average volume per workout, so it reflects how heavy
  // each athlete trains the lift rather than who simply logged it more often.
  const commonRows = commonNames.slice(0, 15).map((name) => ({
    name,
    cells: maps.map((m) => m[name]),
    lead: leadAmong(maps.map((m) => avg(m[name])))
  }));

  const sortFn = sortMode === "workouts"
    ? (a, b) => b.workoutCount - a.workoutCount || b.volumeKg - a.volumeKg || a.name.localeCompare(b.name)
    : (a, b) => b.volumeKg - a.volumeKg || b.setCount - a.setCount || a.name.localeCompare(b.name);

  const topLists = frequencies.map((freq) => [...freq].sort(sortFn).slice(0, 8));
  // Exercises that rank in more than one athlete's top list — marked so they
  // can be paired across the columns at a glance.
  const topCounts = new Map();
  for (const list of topLists) {
    for (const ex of list) topCounts.set(ex.name, (topCounts.get(ex.name) || 0) + 1);
  }
  const sharedLabel = pair ? "In both top lists" : "In more than one top list";

  const renderTop = (list) => (
    <div className={styles.topList}>
      {list.map((ex, i) => {
        const shared = (topCounts.get(ex.name) || 0) > 1;
        const vol = formatWeight(ex.volumeKg, weightUnit);
        const wk = workoutsLabel(ex.workoutCount);
        return (
          <div
            key={`${ex.name}-${sortMode}-${periodDays}`}
            className={`${styles.topItem} ${shared ? styles.shared : ""}`}
            style={{ "--i": i }}
          >
            <span className={styles.rank}>{i + 1}</span>
            <span className={styles.exNameTop} title={shared ? sharedLabel : undefined}>
              {shared && <span className={styles.sharedDot} aria-hidden="true" />}
              {ex.name}
            </span>
            <span className={styles.topStatBlock}>
              <span className={styles.topStatVal}>{sortMode === "workouts" ? wk : vol}</span>
              <span className={styles.topStatSub}>{sortMode === "workouts" ? vol : wk}</span>
            </span>
          </div>
        );
      })}
    </div>
  );

  const many = !pair;
  const columns = { "--n": profiles.length };

  return (
    <div className={styles.wrapper}>
      {commonRows.length > 0 && (
        <div className={styles.panel}>
          <div className={styles.heading}>
            <span className={styles.label}>Common exercises</span>
            <span className={styles.note}>{commonRows.length} shared · total &amp; avg volume</span>
          </div>
          {pair && (
            <div className={styles.commonHeader}>
              <span />
              <span className={styles.colA} style={accentVars(profiles[0].accent)}>@{profiles[0].username}</span>
              <span />
              <span className={styles.colB} style={accentVars(profiles[1].accent)}>@{profiles[1].username}</span>
            </div>
          )}
          <div className={styles.commonList}>
            {commonRows.map(({ name, cells, lead }, i) => (
              <div
                key={`${name}-${periodDays}`}
                className={`${styles.commonRow} ${many ? styles.many : ""}`}
                style={{ "--i": i, ...columns }}
              >
                <span className={styles.exName}>{name}</span>
                {pair ? (
                  <>
                    <ExStats ex={cells[0]} profile={profiles[0]} weightUnit={weightUnit} align="right" />
                    <LeadBadge profiles={profiles} lead={lead} />
                    <ExStats ex={cells[1]} profile={profiles[1]} weightUnit={weightUnit} align="left" />
                  </>
                ) : (
                  <>
                    {cells.map((ex, index) => (
                      <ExStats
                        key={profiles[index].username}
                        ex={ex}
                        profile={profiles[index]}
                        weightUnit={weightUnit}
                        align="right"
                        showName
                      />
                    ))}
                    <LeadBadge profiles={profiles} lead={lead} />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.topSection}>
        <div className={styles.topSectionHead}>
          <span className={styles.label}>Top exercises</span>
          <div className={styles.toggle} role="group" aria-label="Sort top exercises">
            <button
              className={`${styles.toggleBtn} ${sortMode === "volume" ? styles.toggleActive : ""}`}
              onClick={() => setSortMode("volume")}
              aria-pressed={sortMode === "volume"}
            >
              Volume
            </button>
            <button
              className={`${styles.toggleBtn} ${sortMode === "workouts" ? styles.toggleActive : ""}`}
              onClick={() => setSortMode("workouts")}
              aria-pressed={sortMode === "workouts"}
            >
              Workouts
            </button>
          </div>
        </div>
        <div className={styles.topRow} style={columns}>
          {profiles.map((profile, i) => (
            <div className={styles.panel} key={profile.username}>
              <div className={styles.heading}>
                <span className={styles.owner} style={accentVars(profile.accent)}>@{profile.username}</span>
              </div>
              {renderTop(topLists[i])}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
